<template>
  <div
    :class="['dn-skeleton', { 'dn-skeleton-animated': animated }]"
    role="status"
    :aria-label="t('Loading')"
  >
    <div
      v-if="showMedia"
      class="dn-skeleton-media"
      aria-hidden="true"
    />
    <div
      class="dn-skeleton-lines"
      aria-hidden="true"
    >
      <span
        v-for="line in lines"
        :key="line"
        class="dn-skeleton-line"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
const { t } = useI18n();

defineProps({
  lines: {
    type: Number,
    required: false,
    default: 3
  },
  showMedia: {
    type: Boolean,
    required: false,
    default: true
  },
  animated: {
    type: Boolean,
    required: false,
    default: true
  }
});
</script>

<style lang="scss">
.dn-skeleton {
  --_dn-skeleton_grid-template: var(--dn-skeleton_grid-template, 1fr / repeat(12, 1fr));
  --_dn-skeleton_aspect-ratio: var(--dn-skeleton_aspect-ratio, 16 / 9);
  --_dn-skeleton_color: var(--dn-skeleton_color, var(--color-neutral-100));
  --_dn-skeleton_radius: var(--dn-skeleton_radius, var(--border-radius-default-min));
  --_dn-skeleton_gap: var(--dn-skeleton_gap, var(--row-gap-section));
  --_dn-skeleton_lines_grid-template: var(--dn-skeleton_lines_grid-template, repeat(2, min-content) 1fr / 1fr);
  --_dn-skeleton_lines_grid-area: var(--dn-skeleton_lines_grid-area, auto / 1 / span 1 / 13);
  --_dn-skeleton_lines_gap: var(--dn-skeleton_lines_gap, var(--spacing-row-gap-min));
  --_dn-skeleton_lines_border-bottom-width: var(--dn-skeleton_lines_border-bottom-width, 0);
  --_dn-skeleton_lines_border-bottom-color: var(--dn-skeleton_lines_border-bottom-color, var(--color-page-divider));
  --_dn-skeleton_lines_border-right-width: var(--dn-skeleton_lines_border-right-width, 0);
  --_dn-skeleton_lines_border-right-color: var(--dn-skeleton_lines_border-right-color, var(--color-page-divider));
  --_dn-skeleton_lines_padding: var(--dn-skeleton_lines_padding, 0);
  --_dn-skeleton_line-height: var(--dn-skeleton_line-height, var(--spacing-row-gap-min));
  --_dn-skeleton_line-width: var(--dn-skeleton_line-width, 70px);
  --_dn-skeleton_media_grid-area: var(--dn-skeleton_media_grid-area, 1 / 1 / 6 / 13);
  --_dn-skeleton_shimmer-animation: var(--dn-skeleton_shimmer-animation, none);
  --_dn-skeleton_shimmer-content: var(--dn-skeleton_shimmer-content, none);

  display: grid;
  grid-template: var(--_dn-skeleton_grid-template);
  gap: var(--_dn-skeleton_gap);

  &-animated {
    --dn-skeleton_shimmer-content: '';
    --dn-skeleton_shimmer-animation: dn-skeleton-shimmer 1.5s linear infinite;
  }

  &-media,
  &-line {
    position: relative;
    overflow: hidden;
    background-color: var(--_dn-skeleton_color);
    border-radius: var(--_dn-skeleton_radius);

    &::after {
      content: var(--_dn-skeleton_shimmer-content);
      position: absolute;
      inset: 0;
      background: linear-gradient(90deg, transparent 0%, var(--color-neutral-50) 50%, transparent 100%);
      transform: translateX(-100%);
      animation: var(--_dn-skeleton_shimmer-animation);
    }
  }

  &-media {
    grid-area: var(--_dn-skeleton_media_grid-area);
    aspect-ratio: var(--_dn-skeleton_aspect-ratio);
  }

  &-lines {
    display: grid;
    grid-template: var(--_dn-skeleton_lines_grid-template);
    grid-area: var(--_dn-skeleton_lines_grid-area);
    align-content: flex-start;
    gap: calc(var(--_dn-skeleton_lines_gap) / 2);
    border-bottom: var(--_dn-skeleton_lines_border-bottom-width) solid var(--_dn-skeleton_lines_border-bottom-color);
    border-right: var(--_dn-skeleton_lines_border-right-width) solid var(--_dn-skeleton_lines_border-right-color);
    padding: var(--_dn-skeleton_lines_padding);
  }

  &-line {
    height: var(--_dn-skeleton_line-height);

    &:last-child:nth-child(n + 3) {
      width: var(--_dn-skeleton_line-width);
      align-self: flex-end;
    }
  }
}

@keyframes dn-skeleton-shimmer {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(200%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .dn-skeleton-animated {
    --dn-skeleton_shimmer-animation: none;
  }
}
</style>
